import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Renderer2,
  ViewChild
} from '@angular/core';
import { TournamentTeamPhotosService } from './tournament-team-photos.service';
import {
  componentDestroyed,
  getArrayChunks,
  handleError,
  MediaItem,
  UntilDestroy,
  BannerLocation,
  LeagueBanner
} from '@mtgame/core';
import { takeUntil } from 'rxjs/operators';
import { DataSliderComponent, isTouchDevice } from '@mtgame/ui';
import { getLeagueMediaRoute, getTeamPageRoute } from '@config/routes';
import { isPlatformBrowser } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { combineLatest } from 'rxjs';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-team-photos',
  templateUrl: './tournament-team-photos.component.html',
  styleUrls: [
    './tournament-team-photos.component.scss'
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    TournamentTeamPhotosService
  ]
})
export class TournamentTeamPhotosComponent implements OnInit, OnDestroy {
  @Input()
  leagueId: number;
  @Input()
  teamId: number;
  media: MediaItem[][];
  loading = true;
  @ViewChild('dataSliderComponent', {read: DataSliderComponent})
  dataSliderComponent: DataSliderComponent;
  isBrowser = isPlatformBrowser(this.platformId);
  stopScroll = false;
  mediaOpened: boolean;
  banners: LeagueBanner[] = [];
  counter = 0;

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private tournamentTeamPhotosService: TournamentTeamPhotosService,
    private cdr: ChangeDetectorRef,
    private renderer2: Renderer2,
    private router: Router,
  ) {
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      return;
    }
    combineLatest([
      this.tournamentTeamPhotosService.media$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentTeamPhotosService.banners$.pipe(takeUntil(componentDestroyed(this))),
    ]).subscribe(([media, banners]) => {
      if (media && media.length > 0) {
        this.media = getArrayChunks(media, 6);
      } else {
        this.media = [];
      }
      this.banners = banners.filter(banner => banner.location === BannerLocation.media);
      this.cdr.markForCheck();
    });

    await this.tournamentTeamPhotosService.initialize(this.leagueId, this.teamId);
    this.loading = false;
    this.cdr.markForCheck();
    if (this.isBrowser && !isTouchDevice()) {
      this.initializeAutoScroll();
    }
  }

  initializeAutoScroll(): void {
    if (!this.dataSliderComponent) {
      return;
    }
    this.sliderScroll();
    setTimeout(() => {
      this.renderer2.listen(this.dataSliderComponent.content.nativeElement, 'mouseover', event => {
        this.stopScroll = true;
      });
      this.renderer2.listen(this.dataSliderComponent.content.nativeElement, 'mouseleave', event => {
        this.stopScroll = false;
      });
    });
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.mediaOpened = event.url !== '/';
      }
    });
  }

  sliderScroll() {
    if (this.dataSliderComponent && document.visibilityState === 'visible' && !this.stopScroll && !this.mediaOpened) {
      const bounding = this.dataSliderComponent.content.nativeElement.getBoundingClientRect();
      if (
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.right <= (window.innerWidth || document.documentElement.clientWidth) &&
        bounding.bottom <= (window.innerHeight || document.documentElement.clientHeight)
      ) {
        this.dataSliderComponent.content.nativeElement.scrollBy(1, 0);
      }
    }
    setTimeout(() => this.sliderScroll(), 10);
  }

  async loadNextPage(): Promise<void> {
    if (this.loading) {
      return;
    }
    this.counter++;
    this.loading = true;
    if (this.counter > 1) {
      this.stopScroll = true;
    }
    setTimeout(() => {
      this.cdr.markForCheck();
    });
    try {
      const images = await this.tournamentTeamPhotosService.loadNextPage();
      if (images.length === 0) {
        this.loading = false;
      }
    } catch (e) {
      handleError(e);
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
  }

  slideLeft(): void {
    if (this.dataSliderComponent) {
      this.stopScroll = true;
      this.dataSliderComponent.slideLeft();
    }
  }

  slideRight(): void {
    if (this.dataSliderComponent) {
      this.stopScroll = true;
      this.counter = 0;
      this.dataSliderComponent.slideRight();
      setTimeout(() => {
        this.stopScroll = false;
      }, 250);
    }
  }

  getLeagueMediaRoute(): string[] {
    return getLeagueMediaRoute();
  }

  getBannerId(idx: number): number {
    // зацикливаем рекламные баннеры
    return idx % this.banners.length;
  }

  getPhotoDetailRoute(): string {
    return getTeamPageRoute(this.teamId).join('/').replace('//', '/') + '/photo/:mediaId';
  }
}
